/**
 * Custom-Excel auto-import for `data_table` fields (hyreslista Phase 3).
 *
 * `useDataTableAutoImport()` returns `{ run, element }`. The editor renders
 * `element` (a hidden file input + the review modal) once, and passes `run`
 * to the field renderer as `dataTableAutoImport`. The renderer's button
 * calls `run(columns)`; the hook:
 *   1. opens the file picker, parses the xlsx in-browser (read-excel-file),
 *   2. asks the backend (Bedrock Nova) to map the uploaded headers onto our
 *      columns — incl. transforms like a monthly rent → årshyra × 12,
 *   3. shows a review modal where the user confirms/edits each mapping,
 *   4. applies the mapping (`applyColumnMapping`) and resolves with the rows
 *      (or null if cancelled).
 *
 * The xlsx lib is dynamic-imported, so it stays out of the main bundle.
 */

import { useCallback, useRef, useState, type ReactElement } from 'react';
import { X } from 'lucide-react';
import {
  applyColumnMapping,
  type ColumnMapping,
  type MappingOp,
} from '@regta/common/data-table-mapping';
import type { DataTableColumn, DataTableRow } from '@hyranai/regta-templates';
import { tenantApi } from '../api.js';

type Pending = {
  columns: ReadonlyArray<DataTableColumn>;
  resolve: (rows: DataTableRow[] | null) => void;
};

type ModalState = {
  columns: ReadonlyArray<DataTableColumn>;
  headers: string[];
  inputRows: Array<Record<string, string | number | null>>;
  mappings: ColumnMapping[];
  source: 'bedrock' | 'heuristic';
};

export function useDataTableAutoImport(): {
  run: (columns: ReadonlyArray<DataTableColumn>) => Promise<DataTableRow[] | null>;
  element: ReactElement;
} {
  const fileRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);

  const run = useCallback(
    (columns: ReadonlyArray<DataTableColumn>) =>
      new Promise<DataTableRow[] | null>((resolve) => {
        pendingRef.current = { columns, resolve };
        setErr(null);
        fileRef.current?.click();
      }),
    [],
  );

  const settle = (rows: DataTableRow[] | null) => {
    pendingRef.current?.resolve(rows);
    pendingRef.current = null;
    setModal(null);
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const pending = pendingRef.current;
    if (!file || !pending) {
      settle(null);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const readXlsxFile = (await import('read-excel-file/browser')).default;
      const rows = (await readXlsxFile(file)) as unknown as Array<
        Array<string | number | boolean | Date | null>
      >;
      const headers = (rows[0] ?? []).map((h) => String(h ?? '').trim());
      if (headers.filter(Boolean).length === 0) throw new Error('Hittade inga kolumnrubriker.');
      const dataRows = rows
        .slice(1)
        .filter((r) => r.some((c) => c != null && String(c).trim() !== ''));
      const inputRows = dataRows.map((r) => {
        const o: Record<string, string | number | null> = {};
        headers.forEach((h, i) => {
          const raw = r[i];
          o[h] =
            raw == null
              ? ''
              : typeof raw === 'number'
                ? raw
                : raw instanceof Date
                  ? raw.toISOString().slice(0, 10)
                  : String(raw);
        });
        return o;
      });
      const sampleRows = dataRows
        .slice(0, 5)
        .map((r) => headers.map((_, i) => (r[i] == null ? '' : String(r[i]))));
      const cols = pending.columns.filter((c) => !c.computed);
      const res = await tenantApi.hyreslista.suggest_mapping({
        target_columns: cols.map((c) => ({
          key: c.key,
          label: c.label,
          kind: c.kind,
          description: c.description,
        })),
        input_headers: headers,
        sample_rows: sampleRows,
      });
      setModal({ columns: cols, headers, inputRows, mappings: res.mappings, source: res.source });
    } catch (er) {
      setErr((er as Error).message || 'Kunde inte läsa filen.');
      settle(null);
    } finally {
      setBusy(false);
    }
  };

  const patchMapping = (target: string, patch: Partial<ColumnMapping>) =>
    setModal((m) =>
      m
        ? { ...m, mappings: m.mappings.map((x) => (x.target === target ? { ...x, ...patch } : x)) }
        : m,
    );

  const confirm = () => {
    const m = modal;
    if (!m) return settle(null);
    const rows = applyColumnMapping(
      m.inputRows,
      m.mappings,
      m.columns.map((c) => ({ key: c.key, label: c.label, kind: c.kind })),
    );
    settle(rows);
  };

  const element = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(e) => void onFile(e)}
        className="hidden"
      />
      {busy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm text-stone-700 shadow-xl">
            Läser och mappar kolumner…
          </div>
        </div>
      )}
      {err && !modal && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 shadow">
          {err}
          <button type="button" className="ml-2 underline" onClick={() => setErr(null)}>
            stäng
          </button>
        </div>
      )}
      {modal && (
        <MappingReviewModal
          modal={modal}
          onPatch={patchMapping}
          onConfirm={confirm}
          onCancel={() => settle(null)}
        />
      )}
    </>
  );

  return { run, element };
}

const OP_LABEL: Record<MappingOp, string> = {
  none: 'Ingen',
  multiply: '× (gånger)',
  divide: '÷ (delat)',
};

function MappingReviewModal({
  modal,
  onPatch,
  onConfirm,
  onCancel,
}: {
  modal: ModalState;
  onPatch: (target: string, patch: Partial<ColumnMapping>) => void;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactElement {
  const firstRow = modal.inputRows[0];
  const unmapped = modal.mappings.filter((m) => !m.source).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-3xl flex-col gap-3 overflow-hidden rounded-lg border border-stone-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-stone-800">
            Granska kolumnmappning
          </h2>
          <button type="button" onClick={onCancel} className="rounded p-1 text-stone-400 hover:bg-stone-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[11px] text-stone-500">
          {modal.source === 'bedrock'
            ? 'Förslaget kommer från automatisk mappning. Kontrollera och justera vid behov.'
            : 'Automatisk tjänst ej tillgänglig — enkel matchning användes. Kontrollera noga.'}
          {unmapped > 0 && (
            <span className="ml-1 text-amber-700">{unmapped} kolumn(er) saknar källa.</span>
          )}
        </p>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-stone-500">
                <th className="border-b border-stone-200 py-1 pr-2">Vår kolumn</th>
                <th className="border-b border-stone-200 py-1 pr-2">Källa (din rubrik)</th>
                <th className="border-b border-stone-200 py-1 pr-2">Beräkning</th>
                <th className="border-b border-stone-200 py-1 pr-2">Resultat (rad 1)</th>
              </tr>
            </thead>
            <tbody>
              {modal.columns.map((col) => {
                const m = modal.mappings.find((x) => x.target === col.key);
                const op = m?.op ?? 'none';
                const numeric = col.kind === 'currency' || col.kind === 'number';
                const preview =
                  firstRow && m
                    ? applyColumnMapping([firstRow], [m], [
                        { key: col.key, label: col.label, kind: col.kind },
                      ])[0]?.[col.key]
                    : '';
                return (
                  <tr key={col.key} className="align-top">
                    <td className="border-b border-stone-100 py-1.5 pr-2 font-medium text-stone-800">
                      {col.label}
                      {!m?.source && (
                        <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] text-amber-800">
                          ej mappad
                        </span>
                      )}
                    </td>
                    <td className="border-b border-stone-100 py-1.5 pr-2">
                      <select
                        value={m?.source ?? ''}
                        onChange={(e) =>
                          onPatch(col.key, { source: e.target.value || null })
                        }
                        className="w-full rounded border border-stone-300 bg-white px-1 py-0.5 text-[11px]"
                      >
                        <option value="">(ingen)</option>
                        {modal.headers.map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="border-b border-stone-100 py-1.5 pr-2">
                      {numeric && m?.source ? (
                        <div className="flex items-center gap-1">
                          <select
                            value={op}
                            onChange={(e) =>
                              onPatch(col.key, {
                                op: e.target.value as MappingOp,
                                factor:
                                  e.target.value === 'none' ? null : m?.factor ?? 12,
                              })
                            }
                            className="rounded border border-stone-300 bg-white px-1 py-0.5 text-[11px]"
                          >
                            {(['none', 'multiply', 'divide'] as MappingOp[]).map((o) => (
                              <option key={o} value={o}>
                                {OP_LABEL[o]}
                              </option>
                            ))}
                          </select>
                          {op !== 'none' && (
                            <input
                              type="number"
                              step="0.01"
                              value={m?.factor ?? ''}
                              onChange={(e) =>
                                onPatch(col.key, {
                                  factor: e.target.value === '' ? null : Number(e.target.value),
                                })
                              }
                              className="w-16 rounded border border-stone-300 px-1 py-0.5 text-[11px]"
                            />
                          )}
                        </div>
                      ) : (
                        <span className="text-stone-400">—</span>
                      )}
                    </td>
                    <td className="border-b border-stone-100 py-1.5 pr-2 font-mono text-stone-600">
                      {preview === '' || preview == null ? '—' : String(preview)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-stone-200 pt-3">
          <span className="text-[11px] text-stone-500">
            {modal.inputRows.length} rad(er) importeras.
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800"
            >
              Importera {modal.inputRows.length} rad(er)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
